import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNotificationContext } from "../../../../../contexts/NotificationContext.ts";
import biClientShared from "../../../../api/biClient.ts";
import { ConditionField, DimensionDictionary, ItemDataType } from "../../../../api/biClient.types.ts";
import { useSharedReportingLocalization } from "../../../../hooks/useSharedReportingLocalization.ts";
import { unique } from "../../../../utils/unique.ts";

export default function useDeferredDictionaryLoading(
  dataSetId: string,
  area: {
    values: ConditionField[];
    updateItem: (field: ConditionField, changes: Partial<ConditionField>) => void;
  },
  onLoadDictionaries?: (result: { [key: string]: DimensionDictionary }[]) => void,
  shouldNotLoadDictionary?: boolean
) {
  const { metadata: locale } = useSharedReportingLocalization();
  const { sendNotificationError } = useNotificationContext();

  const dimensionsToLoadDictionaries = useMemo(() => {
    if (shouldNotLoadDictionary) {
      return [];
    }
    return area.values
      .filter((value) => value.meta)
      .filter(
        (value) =>
          value.meta.type === ItemDataType.General &&
          value.dictionary?.loaded !== true &&
          value.dictionary?.loading !== true &&
          value.dictionary?.failedToLoad !== true &&
          value.meta.dictionary.length === 0
      )
      .map((v) => v.meta.name)
      .filter(unique);
  }, [area.values, shouldNotLoadDictionary]);

  const changeLoadingState = useCallback(
    (names: string[], loading: boolean, loaded: boolean, failedToLoad = false) => {
      names.forEach((name) => {
        const fields = area.values.filter((v) => v.meta.name === name);
        if (fields !== undefined) {
          fields.forEach((field) => {
            area.updateItem(field, { dictionary: { loading, loaded, failedToLoad } });
          });
        }
      });
    },
    [area]
  );
  const dataSetIdRef = useRef(dataSetId);
  dataSetIdRef.current = dataSetId;

  useEffect(() => {
    const cancelled = false;
    if (dimensionsToLoadDictionaries.length > 0) {
      changeLoadingState(dimensionsToLoadDictionaries, true, false);
      biClientShared
        .loadDimensionDictionaries(dataSetIdRef.current, ...dimensionsToLoadDictionaries)
        .then((response) => {
          if (cancelled) return;
          if (response.success) {
            changeLoadingState(dimensionsToLoadDictionaries, false, true);
            onLoadDictionaries?.(response.data);
          } else {
            sendNotificationError(locale.failed_to_load_dimension);
            changeLoadingState(dimensionsToLoadDictionaries, false, false, true);
          }
        })
        .catch(() => {
          if (cancelled) return;
          changeLoadingState(dimensionsToLoadDictionaries, false, false, true);
          sendNotificationError(locale.failed_to_load_dimension);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionsToLoadDictionaries]);
}
